/**
 * Os painéis que não são o conteúdo principal da 1ª tela (a coluna direita sem foco, "Mais sobre a apuração" e o gráfico
 * "Ao longo da apuração") montam depois da 1ª pintura do app (DECISOES §38): o 1º desenho do herói e da coluna
 * principal sai antes, numa tarefa menor, e esse desenho é o LCP no celular. O sinal nasce `true`, então os testes e a
 * página do replay não mudam; só a entrada do site ao vivo (main.tsx) o põe em `false` no boot.
 */
import { effect, signal } from '@preact/signals';
import { feed, liveView } from '../state/store';

/** (app/firstPaint.ts) false só entre o boot do site ao vivo e a 1ª pintura do app. */
export const firstPaintDone = signal(true);

/** Chamada por main.tsx antes do 1º render: os painéis esperam o quadro seguinte à 1ª pintura. */
export function deferUntilFirstPaint(): void {
  if (typeof requestAnimationFrame !== 'function') return;
  firstPaintDone.value = false;
  // o requestAnimationFrame roda antes da pintura do quadro; o setTimeout, depois dela
  requestAnimationFrame(() => {
    setTimeout(() => {
      firstPaintDone.value = true;
    }, 0);
  });
}

/**
 * Espera o esboço do boot ser pintado E registrado pelo navegador antes de o app montar (DECISOES §40). Não basta o
 * quadro seguinte: o Chrome só registra o candidato a LCP (o maior conteúdo da tela) quando confirma a pintura, um pouco
 * depois; se o app troca o esboço antes disso, o título do esboço é descartado, e o LCP passa a ser o título do app,
 * depois de todo o JS (o trace do Lighthouse mostrou o esboço na tela aos 177 ms e nenhum candidato até o app, aos
 * 320 ms). Por isso espera o registro do 1º conteúdo pintado (`paint` / `first-contentful-paint`), com teto de 200 ms
 * para nunca prender o boot; sem esboço na página ou sem PerformanceObserver, segue na hora. O app troca o esboço na
 * mesma tarefa, sem quadro intermediário (AC-17.2-10).
 */
export function afterSkeletonPaint(root: Element, capMs = 200): Promise<void> {
  if (root.childElementCount === 0 || typeof PerformanceObserver !== 'function') return Promise.resolve();
  if (performance.getEntriesByName('first-contentful-paint').length > 0) return Promise.resolve();
  return new Promise((done) => {
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      obs.disconnect();
      clearTimeout(timer);
      done();
    };
    const obs = new PerformanceObserver((list) => {
      if (list.getEntriesByName('first-contentful-paint').length > 0) finish();
    });
    try {
      obs.observe({ type: 'paint', buffered: true });
    } catch {
      // navegador sem o tipo `paint`: segue pelo teto
    }
    const timer = setTimeout(finish, capMs);
  });
}

/**
 * Espera o 1º snapshot aceito antes de o app montar, com teto (DECISOES §40). O feed passa a ser ligado antes do 1º
 * render (main.tsx), e a pré-busca do snapshot já chegou quase sempre quando o JS termina; sem esperar o processamento
 * dele (validação e `selectView`), o app pintava as disputas dos Governadores em ordem alfabética (sem eleitorado) e,
 * um instante depois, as reordenava pelo eleitorado do snapshot: a troca recria as linhas no DOM, e a repintura era o
 * LCP de `#/governadores`. Falha do feed ou teto vencido: monta na hora, no estado "Conectando" de sempre (§2.2.3).
 */
export function firstSnapshotOrCap(capMs = 800): Promise<void> {
  if (liveView.peek() !== null) return Promise.resolve();
  return new Promise((done) => {
    let finished = false;
    let dispose: (() => void) | null = null;
    const finish = () => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      dispose?.();
      done();
    };
    const timer = setTimeout(finish, capMs);
    dispose = effect(() => {
      if (liveView.value !== null || feed.value.failures > 0) queueMicrotask(finish);
    });
    if (finished) dispose();
  });
}

/**
 * Espera o navegador registrar um conteúdo do app pintado depois de `since` (uma entrada `largest-contentful-paint`),
 * com teto (DECISOES §40). O chunk do mapa é pedido depois disso: em `#/presidente/sp`, ele era baixado e avaliado
 * entre a 1ª pintura do app (aos ~250 ms no trace do Lighthouse) e o registro dela (~355 ms), e o download e o script do
 * mapa entravam no LCP. Sem o tipo no navegador, segue na hora; sem entrada nova (o maior conteúdo continua o do
 * esboço), segue pelo teto.
 */
export function afterContentPaint(since: number, capMs = 300): Promise<void> {
  if (
    typeof PerformanceObserver !== 'function' ||
    !PerformanceObserver.supportedEntryTypes?.includes('largest-contentful-paint')
  ) {
    return Promise.resolve();
  }
  return new Promise((done) => {
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      obs.disconnect();
      clearTimeout(timer);
      done();
    };
    const obs = new PerformanceObserver((list) => {
      if (list.getEntries().some((e) => e.startTime >= since)) finish();
    });
    const timer = setTimeout(finish, capMs);
    obs.observe({ type: 'largest-contentful-paint', buffered: true });
  });
}
